/**
 * Every TanStack Query key and lifetime (mission 6 T011; NFR-005, P.7, P.8,
 * C-010; research §1.8; post-plan C3, C10, C11, C15; post-tasks E17, P9).
 * No screen spells a key: it imports a factory from here.
 *
 * Rules.
 * - Only `['run', id]` polls, every POLL_MS while the state is not terminal,
 *   and it stops on an error, on a terminal state, and never refetches on
 *   focus. A transient failure (a transport error, a 5xx or a 429) is retried
 *   up to POLL_RETRIES times at the poll interval before it counts as the
 *   error that stops the poll (mission 6 review follow-up 15, A-2). The state screens read the dispatcher's query; nothing polls twice.
 * - The findings views wait for `ready` (`enabled: ready`), and a body whose
 *   `rule_set_version` is `not-run` throws NotReadyError, so the not-ready shape
 *   is never cached (C11).
 * - Run-level invalidation is exact, so the findings views are not refetched (C15).
 * - Paging holds one page of at most 50; a cursor stack is component state,
 *   never a key.
 *
 * One owner per credit event (P9, NFR-005: at most one balance read per
 * navigation plus one per observed run event):
 * - cancel, and any terminal transition seen by polling: RunPage's observed
 *   transition to a terminal state is the only trigger (`invalidateCredits`);
 *   `applyCancel` never touches `keys.credits`;
 * - start: navigation is the only trigger (the shell's credit chip invalidates
 *   on a pathname change); the new-run screen invalidates `keys.runsRoot` only.
 *
 * Mission 7 WP07 (T040; NFR-008; research §1.24; squad U10, U16) adds the
 * bureaus, processing-terms and transmission reads:
 * - `bureaus` and `processing-terms` are read once per navigation (staleTime 0)
 *   and never refetched on focus (U16);
 * - the two logs poll every POLL_MS while an entry on the visible page is
 *   `pending`, stop when none is and on an error, keep their data, and retry a
 *   transient failure like the run poll (A-2);
 * - `invalidateTransmissions` is the transmit's one refresh: the run's log, the
 *   tenant log and the history (its `latest_transmission`), never the credits
 *   (TX.8, C-005).
 * Keys hold IDs and filters only, never a secret.
 *
 * Mission 8 WP07 (T033; NFR-010, C-005; research §1.21; contracts/ui-api.md
 * "Query keys"; squad C13, U11) adds the assistant, under `['assistant', …]`,
 * outside the `['run', id]` prefix:
 * - usage and the conversation are read once per panel open (one observer at
 *   the panel root, `refetchOnMount: 'always'` over `staleTime: Infinity`),
 *   never on focus and never by a poll; the conversation is removed on close
 *   (`removeConversation`), so a reopen reads page 1 only;
 * - the latest run for a rule is fresh for five minutes (OR-8);
 * - the three reads never retry an ApiError below 500 and retry a 503 or a
 *   transport error once (`assistantRetry`);
 * - an ask is never re-sent (`retry: 0`): its answer prepends to page 1 and
 *   sets the usage, so the meter moves in the answer's render with no usage
 *   read; a 429 sets the usage from its members; a 503 writes nothing; nothing
 *   invalidates the credits (C-005);
 * - `runTransmissionsAll` walks every page of a run's transmissions (WP11, UI-3).
 * The question travels only in the ask's body, never in a key.
 *
 * Mission 9 WP11 (T066; NFR-012; contracts/ui-api.md "Query keys"; squad S22,
 * U8, U25) adds billing:
 * - `['billing']` is read on the mount of sheets 08 and 50, fresh for 30 s,
 *   never on focus, and a 4xx is an answer, never retried (`billingRetry`);
 * - `['credits', 'ledger', 'free']` pages the usage ledger by its opaque cursor
 *   (page 1 on mount, older pages on demand; the server pages 50);
 * - every billing mutation writes `['billing']` from its answer and invalidates
 *   `keys.credits` (the prefix, so the ledger too); a purchase also invalidates
 *   every estimate; none is re-sent;
 * - the checkout and the card update leave for the provider inside the
 *   mutation (`leaveForProvider`: `safeProviderUrl`, then `cancelQueries()`,
 *   then `window.location.assign`), so the capability URL never reaches a
 *   query or mutation state (S22, U8).
 *
 * Mission 10 WP15 (T085; FR-017, FR-018; research §1.27 "The spine") adds retention, the lifecycle
 * requests and the audit log:
 * - `['retention', cursor]` and `['audit', filters, cursor]` are infinite pages over the opaque
 *   `next_cursor`, read on the mount of sheets 43, 44, 30 and 31 (the first page carries the window and
 *   its `terms`), never on focus, never retrying a 4xx (`billingRetry`);
 * - `useRetentionMutation` exposes `confirmation`: the 409 `retention-confirmation-required` (D.2) as
 *   `due_count` (the exact N), `runs` (at most 100) and `truncated` (spec amendment 26), for the shortening
 *   dialog; a saved change invalidates the window and the audit log;
 * - `useExportRequestMutation` invalidates the same two;
 * - `useDeleteAccountMutation` clears the whole cache and leaves for `/login?notice=account-closed` on the
 *   202 (the server cleared the session cookies). Its 403 `reauthentication-failed` is an answer the dialog
 *   renders in place: nothing is cleared and nobody is signed out (the session handler acts on the 401
 *   `unauthorized` only);
 * - none is re-sent (`retry: 0`); none touches the credits.
 *
 * Frozen after mission 6 WP02 (T17); mission 7's additions frozen after WP07;
 * mission 8's after its WP07; mission 9's after its WP11; mission 10's after its WP15.
 */
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { TENANT_QUERY_KEY, clearSessionCache } from '@/components/trellis/AuthGate'
import { ApiError, InvalidIdError, apiOrigin } from './api'
import { listAudit, type AuditFilters, type AuditPage } from './api/audit'
import { requestDeletion, requestExport, type DeletionBody, type DeletionResult, type ExportResult } from './api/lifecycle'
import {
  getRetention,
  patchRetention,
  type RetentionPatchBody,
  type RetentionPatchResult,
  type RetentionPreviewRun,
  type RetentionWindow,
} from './api/retention'
import {
  ask,
  conversation,
  latestRun,
  putFeedback,
  usage,
  type AskBody,
  type AskResult,
  type AssistantUsage,
  type AssistantVerdict,
  type ConversationPage,
  type FeedbackResult,
} from './api/assistant'
import {
  cancelPlan,
  getBilling,
  purchaseCredits,
  putBillingEmail,
  startCardUpdate,
  startCheckout,
  switchPlan,
  undoCancel,
  type BillingView,
  type CheckoutBody,
  type PlanId,
  type PurchaseBody,
} from './api/billing'
import { getCredits, getLedger, type LedgerPage, type LedgerParams } from './api/credits'
import {
  getByRecord,
  getByRule,
  getFindings,
  getRuleCounts,
  getTrailer,
  type ByRecordFilters,
  type ByRuleFilters,
  type FindingFilters,
} from './api/findings'
import { getBureaus } from './api/bureaus'
import { getHeaderParams } from './api/headerParams'
import { getLayout, type LayoutVersion } from './api/layout'
import { getRule, listRules } from './api/rules'
import { getRun, isTerminal, listRuns, type Run, type RunFilters } from './api/runs'
import { getProcessingTerms } from './api/terms'
import {
  getRunTransmissions,
  listTransmissions,
  type RunTransmissions,
  type TransmissionFilters,
  type TransmissionsPage,
} from './api/transmissions'
import { getEstimate, getUpload } from './api/uploads'
import { PROBLEM } from './problems'
import { safeProviderUrl } from './provider-url'
import { isDefiniteRefusal, settlePendingPurchase } from './purchase-request'

/** The in-progress poll interval (C-010). */
export const POLL_MS = 2000

const HOUR = 60 * 60 * 1000

export const keys = {
  tenant: TENANT_QUERY_KEY,
  credits: ['credits'] as const,
  layout: (version: LayoutVersion) => ['layout', version] as const,
  headerParams: ['header-params'] as const,
  upload: (id: string) => ['upload', id] as const,
  estimate: (uploadId: string) => ['estimate', uploadId] as const,
  runsRoot: ['runs'] as const,
  runs: (f: RunFilters) => ['runs', { state: f.state, path: f.path, cursor: f.cursor }] as const,
  run: (id: string) => ['run', id] as const,
  ruleCounts: (id: string) => ['run', id, 'rule-counts'] as const,
  byRule: (id: string, f: ByRuleFilters) => ['run', id, 'by-rule', { severity: f.severity, cursor: f.cursor }] as const,
  byRecord: (id: string, f: ByRecordFilters) => ['run', id, 'by-record', { cursor: f.cursor }] as const,
  findings: (id: string, f: FindingFilters) =>
    [
      'run',
      id,
      'findings',
      { rule_id: f.rule_id, severity: f.severity, field: f.field, segment: f.segment, cursor: f.cursor },
    ] as const,
  trailer: (id: string) => ['run', id, 'trailer'] as const,
  rules: ['rules'] as const,
  rule: (id: string) => ['rule', id] as const,
  // Mission 7 (T040).
  bureaus: ['bureaus'] as const,
  processingTerms: ['processing-terms'] as const,
  runTransmissions: (id: string, cursor?: string) => ['run', id, 'transmissions', cursor ?? null] as const,
  transmissionsRoot: ['transmissions'] as const,
  transmissions: (f: TransmissionFilters, cursor?: string) =>
    [
      'transmissions',
      { run: f.run, bureau: f.bureau, result: f.result, from: f.from, to: f.to, q: f.q },
      cursor ?? null,
    ] as const,
  // Mission 8 (T033).
  assistantRoot: ['assistant'] as const,
  assistantUsage: ['assistant', 'usage'] as const,
  assistantConversation: (runId: string) => ['assistant', 'conversation', runId] as const,
  ruleLatestRun: (ruleId: string) => ['assistant', 'latest-run', ruleId] as const,
  runTransmissionsAll: (id: string) => ['run', id, 'transmissions', { all: true }] as const,
  // Mission 9 (T066).
  billing: ['billing'] as const,
  ledger: (params: Pick<LedgerParams, 'include'>) => ['credits', 'ledger', params.include ?? 'entries'] as const,
  estimatesRoot: ['estimate'] as const,
  // Mission 10 (T085). Prefixes: the screens' keys add the starting cursor (and the audit filters).
  retention: ['retention'] as const,
  audit: ['audit'] as const,
}

/** A findings view answered before `ready`; thrown so the shape is never cached (C11). */
export class NotReadyError extends Error {
  constructor() {
    super('run not ready')
    this.name = 'NotReadyError'
  }
}

/**
 * NFR-005's cap on any list the browser renders (mission 6 review follow-up
 * 16, A-3): a longer inline window is cut here and the rest is left to the
 * report.
 */
export const LIST_CAP = 200

/** How many times the run poll retries a transient failure before it stops (A-2). */
export const POLL_RETRIES = 3

/**
 * The run poll's retry (A-2): a transport error, a 5xx or a 429 is transient
 * and retried POLL_RETRIES times; every other answer (a 404, a 401, a refused
 * id) settles at once.
 */
function pollRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof InvalidIdError || error instanceof NotReadyError) return false
  if (error instanceof ApiError && error.problem.status < 500 && error.problem.status !== 429) return false
  return failureCount < POLL_RETRIES
}

/** A 404, a refused id or a not-ready body is an answer; anything else is retried once. */
function retry(failureCount: number, error: unknown): boolean {
  if (error instanceof NotReadyError || error instanceof InvalidIdError) return false
  if (error instanceof ApiError && error.problem.status === 404) return false
  return failureCount < 1
}

function whenReady<T extends { rule_set_version: string }>(fetcher: () => Promise<T>): () => Promise<T> {
  return async () => {
    const body = await fetcher()
    if (body.rule_set_version === 'not-run') throw new NotReadyError()
    return body
  }
}

export function creditsQuery() {
  return queryOptions({ queryKey: keys.credits, queryFn: getCredits, staleTime: Infinity, refetchOnWindowFocus: false })
}

export function layoutQuery(version: LayoutVersion) {
  return queryOptions({ queryKey: keys.layout(version), queryFn: () => getLayout(version), staleTime: Infinity })
}

export function headerParamsQuery() {
  return queryOptions({ queryKey: keys.headerParams, queryFn: getHeaderParams, staleTime: 30_000 })
}

export function uploadQuery(id: string) {
  return queryOptions({ queryKey: keys.upload(id), queryFn: () => getUpload(id), staleTime: 0 })
}

/** Refetched on focus: credits bought in another tab change the answer (spec edge case; not a poll). */
export function estimateQuery(uploadId: string) {
  return queryOptions({
    queryKey: keys.estimate(uploadId),
    queryFn: () => getEstimate(uploadId),
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
}

export function runsQuery(filters: RunFilters) {
  return queryOptions({
    queryKey: keys.runs(filters),
    queryFn: () => listRuns(filters),
    staleTime: 0,
    placeholderData: keepPreviousData,
  })
}

/**
 * The one poll (C10): every POLL_MS until a terminal state or an error. A
 * transient failure is retried at POLL_MS, so a retry never reads the run
 * faster than the poll does (NFR-005), and only a failure that outlasts the
 * retries stops it (A-2).
 */
export function runQuery(id: string) {
  return queryOptions({
    queryKey: keys.run(id),
    queryFn: () => getRun(id),
    refetchInterval: (q): number | false =>
      q.state.status === 'error' || (q.state.data !== undefined && isTerminal(q.state.data.state)) ? false : POLL_MS,
    refetchOnWindowFocus: false,
    retry: pollRetry,
    retryDelay: POLL_MS,
  })
}

export function ruleCountsQuery(id: string, ready: boolean) {
  return queryOptions({
    queryKey: keys.ruleCounts(id),
    queryFn: whenReady(() => getRuleCounts(id)),
    enabled: ready,
    staleTime: Infinity,
    retry,
  })
}

export function byRuleQuery(id: string, filters: ByRuleFilters, ready: boolean) {
  return queryOptions({
    queryKey: keys.byRule(id, filters),
    queryFn: whenReady(() => getByRule(id, filters)),
    enabled: ready,
    staleTime: Infinity,
    retry,
  })
}

export function byRecordQuery(id: string, filters: ByRecordFilters, ready: boolean) {
  return queryOptions({
    queryKey: keys.byRecord(id, filters),
    queryFn: whenReady(() => getByRecord(id, filters)),
    enabled: ready,
    staleTime: Infinity,
    retry,
  })
}

export function findingsQuery(id: string, filters: FindingFilters, ready: boolean) {
  return queryOptions({
    queryKey: keys.findings(id, filters),
    queryFn: whenReady(() => getFindings(id, filters)),
    enabled: ready,
    staleTime: Infinity,
    retry,
  })
}

export function trailerQuery(id: string, ready: boolean) {
  return queryOptions({
    queryKey: keys.trailer(id),
    queryFn: whenReady(() => getTrailer(id)),
    enabled: ready,
    staleTime: Infinity,
    retry,
  })
}

export function rulesQuery() {
  return queryOptions({ queryKey: keys.rules, queryFn: listRules, staleTime: HOUR })
}

export function ruleQuery(id: string) {
  return queryOptions({ queryKey: keys.rule(id), queryFn: () => getRule(id), staleTime: HOUR, retry })
}

/** Read once per navigation, never on focus (NFR-008, U16). */
export function bureausQuery() {
  return queryOptions({ queryKey: keys.bureaus, queryFn: getBureaus, staleTime: 0, refetchOnWindowFocus: false })
}

/** Read once per navigation, never on focus (NFR-008, U16). */
export function processingTermsQuery() {
  return queryOptions({
    queryKey: keys.processingTerms,
    queryFn: getProcessingTerms,
    staleTime: 0,
    refetchOnWindowFocus: false,
  })
}

/** POLL_MS while an entry on the page is pending; false when none is, and on an error (U10). */
function whilePending(q: { state: { status: string; data?: TransmissionsPage | undefined } }): number | false {
  if (q.state.status === 'error' || q.state.data === undefined) return false
  return q.state.data.entries.some((e) => e.result === 'pending') ? POLL_MS : false
}

/** The run's log (sheets 31 and 32): polls while an attempt is pending, and keeps its data. */
export function runTransmissionsQuery(id: string, cursor?: string) {
  return queryOptions({
    queryKey: keys.runTransmissions(id, cursor),
    queryFn: () => getRunTransmissions(id, cursor),
    refetchInterval: whilePending,
    refetchOnWindowFocus: false,
    retry: pollRetry,
    retryDelay: POLL_MS,
    placeholderData: keepPreviousData,
  })
}

/** The tenant log (sheet 32): polls while a visible row is pending, and keeps its data. */
export function transmissionsQuery(filters: TransmissionFilters, cursor?: string) {
  return queryOptions({
    queryKey: keys.transmissions(filters, cursor),
    queryFn: () => listTransmissions(filters, cursor),
    refetchInterval: whilePending,
    refetchOnWindowFocus: false,
    retry: pollRetry,
    retryDelay: POLL_MS,
    placeholderData: keepPreviousData,
  })
}

/**
 * After a transmit (or a retry): the run's log on every cursor, the tenant log,
 * and the history for `latest_transmission`. The credits key is never touched (TX.8).
 */
export async function invalidateTransmissions(qc: QueryClient, runId?: string): Promise<void> {
  await Promise.all([
    ...(runId === undefined ? [] : [qc.invalidateQueries({ queryKey: ['run', runId, 'transmissions'] })]),
    qc.invalidateQueries({ queryKey: keys.transmissionsRoot }),
    qc.invalidateQueries({ queryKey: keys.runsRoot }),
  ])
}

/** Refetch the run alone; its findings views stay cached (C15). */
export function invalidateRun(qc: QueryClient, id: string): Promise<void> {
  return qc.invalidateQueries({ queryKey: keys.run(id), exact: true })
}

/** RunPage's one credit refresh on an observed transition to terminal (P9). */
export function invalidateCredits(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: keys.credits, exact: true })
}

/**
 * Write a cancel answer: first cancel any in-flight poll, so a late
 * `analyzing` cannot overwrite it (C10), then write the body. The balance is
 * not touched here; RunPage's terminal transition refreshes it (P9).
 */
export async function applyCancel(qc: QueryClient, id: string, body: Run): Promise<void> {
  await qc.cancelQueries({ queryKey: keys.run(id), exact: true })
  qc.setQueryData(keys.run(id), body)
}

// ---------------------------------------------------------------------------
// Mission 8 WP07 T033 (NFR-010, C-005; research §1.21; squad C13, U11).

const FIVE_MINUTES = 5 * 60 * 1000

/**
 * The assistant reads' retry (C13, U11.2): never an ApiError below 500 (a 4xx is an answer) or a refused
 * id, at most once for a 503 or a transport error.
 */
export function assistantRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof InvalidIdError) return false
  return !(error instanceof ApiError && error.problem.status < 500) && failureCount < 1
}

/** The cycle's usage: one read per panel open, at the panel root (U11.1); never on focus, never polled. */
export function usageQuery() {
  return queryOptions({
    queryKey: keys.assistantUsage,
    queryFn: usage,
    staleTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: assistantRetry,
  })
}

/** A run's conversation, newest first: page 1 on open, older pages by cursor on scroll (FR-006, NFR-010). */
export function conversationInfiniteQuery(runId: string) {
  return infiniteQueryOptions({
    queryKey: keys.assistantConversation(runId),
    queryFn: ({ pageParam }) => conversation(runId, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (last: ConversationPage) => last.next_cursor,
    staleTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: assistantRetry,
  })
}

/** The catalog's latest run for a rule, read on row expand (FR-013, OR-8). */
export function latestRunQuery(ruleId: string) {
  return queryOptions({
    queryKey: keys.ruleLatestRun(ruleId),
    queryFn: () => latestRun(ruleId),
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,
    retry: assistantRetry,
  })
}

type ConversationData = InfiniteData<ConversationPage, string | null>

/** Close: drop every page of the run's conversation, so a reopen reads page 1 only (research §1.21). */
export function removeConversation(qc: QueryClient, runId: string): void {
  qc.removeQueries({ queryKey: keys.assistantConversation(runId), exact: true })
}

/**
 * Ask (FR-004, SAT.1, SAT.5). Never re-sent (`retry: 0`). The answer prepends to page 1 of the asked run's
 * conversation, or, with no page cached, invalidates that conversation (mission 8 review UI1), and sets
 * the usage (the meter moves in the answer's render, with no usage read); a 429 sets the cached usage from
 * its members (`used` is the settled count, C5), and adds nothing when no usage is cached, since it
 * carries no cycle start; any other refusal, a 503 included, writes nothing. The credits are never
 * touched (C-005).
 */
export function useAskMutation() {
  const qc = useQueryClient()
  return useMutation<AskResult, Error, AskBody>({
    mutationFn: ask,
    retry: 0,
    onSuccess: (result, body) => {
      const key = keys.assistantConversation(body.run_id)
      if (qc.getQueryData<ConversationData>(key)?.pages[0] === undefined) {
        // Mission 8 review UI1: no page to prepend to (the read failed or has not landed), so one recovery
        // read, which includes the stored answer; a pending read is cancelled and read again. Nothing is
        // invented for a run with no conversation query.
        void qc.invalidateQueries({ queryKey: key, exact: true })
      } else {
        qc.setQueryData<ConversationData>(key, (data) => {
          const [first, ...rest] = data?.pages ?? []
          if (data === undefined || first === undefined) return data
          return { ...data, pages: [{ ...first, messages: [result.message, ...first.messages] }, ...rest] }
        })
      }
      qc.setQueryData<AssistantUsage>(keys.assistantUsage, result.usage)
    },
    onError: (error) => {
      if (!(error instanceof ApiError) || error.problem.status !== 429) return
      const { used, ceiling, resets_at: resetsAt } = error.problem
      if (used === undefined || ceiling === undefined || resetsAt === undefined) return
      qc.setQueryData<AssistantUsage>(keys.assistantUsage, (old) =>
        old === undefined ? undefined : { ...old, used, ceiling, resets_at: resetsAt },
      )
    },
  })
}

/** A verdict on one answer (FR-011, SAT.8): the message's `feedback` in the run's cached pages, nothing else. */
export function useFeedbackMutation(runId: string) {
  const qc = useQueryClient()
  return useMutation<FeedbackResult, Error, { messageId: string; verdict: AssistantVerdict }>({
    mutationFn: ({ messageId, verdict }) => putFeedback(messageId, verdict),
    retry: 0,
    onSuccess: (result) => {
      qc.setQueryData<ConversationData>(keys.assistantConversation(runId), (data) =>
        data === undefined
          ? data
          : {
              ...data,
              pages: data.pages.map((p) => ({
                ...p,
                messages: p.messages.map((m) => (m.id === result.message_id ? { ...m, feedback: result.verdict } : m)),
              })),
            },
      )
    },
  })
}

/**
 * Every page of a run's transmissions (WP11, UI-3): the consumer walks `fetchNextPage` to the cursor's end.
 * Keyed under the run's transmissions prefix, so `invalidateTransmissions` refreshes it; it never polls.
 */
export function runTransmissionsAll(runId: string) {
  return infiniteQueryOptions({
    queryKey: keys.runTransmissionsAll(runId),
    queryFn: ({ pageParam }) => getRunTransmissions(runId, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (last: RunTransmissions) => last.next_cursor,
    refetchOnWindowFocus: false,
    retry,
  })
}

// ---------------------------------------------------------------------------
// Mission 9 WP11 T066 (NFR-012; contracts/ui-api.md "Query keys"; squad S22, U8, U25).

/** The billing reads' retry: a 4xx is an answer (a 402 while pending, a 409), a 503 or a transport error once. */
export const billingRetry = assistantRetry

/** GET /v1/billing, on the mount of sheets 08 and 50: fresh for 30 s, never on focus (NFR-012). */
export function billingQuery() {
  return queryOptions({
    queryKey: keys.billing,
    queryFn: getBilling,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: billingRetry,
  })
}

/** The usage ledger, newest first: page 1 on mount, older pages by cursor on demand (FR-012; amendment 6). */
export function ledgerQuery(params: Pick<LedgerParams, 'include'>) {
  return infiniteQueryOptions({
    queryKey: keys.ledger(params),
    queryFn: ({ pageParam }) => getLedger({ ...params, cursor: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: LedgerPage) => last.next_cursor ?? null,
    refetchOnWindowFocus: false,
    retry: billingRetry,
  })
}

/** A billing mutation's answer: written to `['billing']`, then the balance (and its ledger) read again. */
function writeBilling(qc: QueryClient, update: (old: BillingView | undefined) => BillingView | undefined): void {
  qc.setQueryData<BillingView>(keys.billing, update)
  void qc.invalidateQueries({ queryKey: keys.credits })
}

/**
 * Leave for the provider's page (U8, S22): the URL is checked first (an unsafe one throws
 * `UnsafeProviderUrlError` and nothing is cancelled), every query is cancelled so no late answer
 * writes into a page being left, then the browser navigates.
 *
 * The mock's relative `/dev/stripe/...` path lives on the API. When the API is on another host
 * (an absolute `VITE_API_BASE`, as on trellis.bloomcredit.xyz), the path is resolved against the
 * API origin; resolving it against the SPA's own host shows the SPA's error page (SC-36222). With
 * a relative base, the Vite proxy serves `/dev` on the page origin and the path stays relative.
 */
export async function leaveForProvider(qc: QueryClient, url: string): Promise<void> {
  const safe = safeProviderUrl(url)
  await qc.cancelQueries()
  const origin = apiOrigin()
  window.location.assign(origin === window.location.origin ? safe : new URL(safe, origin).href)
}

/** Cancel at period end (FR-009): the answer is the billing body. */
export function useCancelMutation() {
  const qc = useQueryClient()
  return useMutation<BillingView, Error, void>({
    mutationFn: () => cancelPlan(),
    retry: 0,
    onSuccess: (view) => writeBilling(qc, () => view),
  })
}

/** Keep the plan (FR-009). */
export function useUndoCancelMutation() {
  const qc = useQueryClient()
  return useMutation<BillingView, Error, void>({
    mutationFn: () => undoCancel(),
    retry: 0,
    onSuccess: (view) => writeBilling(qc, () => view),
  })
}

/** Switch at the next renewal (FR-010). */
export function usePlanSwitchMutation() {
  const qc = useQueryClient()
  return useMutation<BillingView, Error, PlanId>({
    mutationFn: (plan) => switchPlan(plan),
    retry: 0,
    onSuccess: (view) => writeBilling(qc, () => view),
  })
}

/**
 * Buy a block (FR-006): the answer's credits and block are written into `['billing']` (a repeat with the
 * same request id replaces its block), and every estimate is read again, since the balance it names moved.
 * A success or a definite refusal settles the pending request id here, not in the component, so it is
 * cleared even when the component that started the purchase unmounted first (audit A25a).
 */
export function usePurchaseMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PurchaseBody) => purchaseCredits(body),
    retry: 0,
    onError: (error, body) => {
      if (isDefiniteRefusal(error)) settlePendingPurchase(body.request_id)
    },
    onSuccess: (result, body) => {
      settlePendingPurchase(body.request_id)
      writeBilling(qc, (old) =>
        old === undefined
          ? old
          : { ...old, credits: result.credits, blocks: [result.block, ...old.blocks.filter((b) => b.id !== result.block.id)] },
      )
      void qc.invalidateQueries({ queryKey: keys.estimatesRoot })
    },
  })
}

/** The billing email (PUT /v1/billing/email). */
export function useBillingEmailMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (billingEmail: string) => putBillingEmail(billingEmail),
    retry: 0,
    onSuccess: (result) => writeBilling(qc, (old) => (old === undefined ? old : { ...old, billing_email: result.billing_email })),
  })
}

/** Start the checkout and leave for it (FR-003); the mutation's data is nothing, so the URL is held nowhere. */
export function useCheckoutMutation() {
  const qc = useQueryClient()
  return useMutation<void, Error, CheckoutBody>({
    mutationFn: async (body) => leaveForProvider(qc, (await startCheckout(body)).checkout_url),
    retry: 0,
  })
}

/** Start the card update and leave for it (FR-007). */
export function useCardUpdateMutation() {
  const qc = useQueryClient()
  return useMutation<void, Error, void>({
    mutationFn: async () => leaveForProvider(qc, (await startCardUpdate()).card_update_url),
    retry: 0,
  })
}

// Mission 10 WP15 T085 (FR-017, FR-018; research §1.27 "The spine"; contracts/retention-api.md,
// tenant-lifecycle-api.md, audit-api.md).

/** The audit filters as a key: the four members only, an absent type list as empty (no secret, no URL). */
function auditKeyFilters(filters: AuditFilters) {
  return { types: [...(filters.types ?? [])], from: filters.from, to: filters.to, q: filters.q }
}

/**
 * GET /v1/tenant/retention by pages, from `cursor` (page 1 when absent): the window, its `terms` and the
 * runs still holding Class 1, older pages on demand (FR-006). Sheets 30 and 31 read the first page.
 */
export function retentionQuery(cursor?: string) {
  return infiniteQueryOptions({
    queryKey: [...keys.retention, cursor ?? null] as const,
    queryFn: ({ pageParam }) => getRetention(pageParam ?? undefined),
    initialPageParam: (cursor ?? null) as string | null,
    getNextPageParam: (last: RetentionWindow) => last.next_cursor ?? null,
    refetchOnWindowFocus: false,
    retry: billingRetry,
  })
}

/** GET /v1/audit by pages, newest first, under the sheet 44 filters (FR-016). */
export function auditQuery(filters: AuditFilters, cursor?: string) {
  return infiniteQueryOptions({
    queryKey: [...keys.audit, auditKeyFilters(filters), cursor ?? null] as const,
    queryFn: ({ pageParam }) => listAudit(filters, pageParam ?? undefined),
    initialPageParam: (cursor ?? null) as string | null,
    getNextPageParam: (last: AuditPage) => last.next_cursor ?? null,
    refetchOnWindowFocus: false,
    retry: billingRetry,
  })
}

/** The shortening dialog's data: the 409's exact count, the listed runs and whether the list is short. */
export interface RetentionConfirmation {
  due_count: number
  runs: RetentionPreviewRun[]
  truncated: boolean
}

/** The confirmation a `retention-confirmation-required` (D.2) refusal carries; null for anything else. */
export function retentionConfirmation(error: unknown): RetentionConfirmation | null {
  if (!(error instanceof ApiError)) return null
  const { problem } = error
  if (problem.type !== PROBLEM.RETENTION_CONFIRMATION_REQUIRED || problem.status !== 409) return null
  return { due_count: problem.due_count ?? 0, runs: problem.runs ?? [], truncated: problem.truncated === true }
}

/** A change to the window or an export request moves the window and appends to the audit log. */
function invalidateRetention(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: keys.retention })
  void qc.invalidateQueries({ queryKey: keys.audit })
}

/**
 * PATCH the window (FR-005). `confirmation` is the shortening refusal's data while the last attempt was
 * that refusal, else null; the screen re-sends with `confirm_due_count: confirmation.due_count`.
 */
export function useRetentionMutation() {
  const qc = useQueryClient()
  const mutation = useMutation<RetentionPatchResult, Error, RetentionPatchBody>({
    mutationFn: (body) => patchRetention(body),
    retry: 0,
    onSuccess: () => invalidateRetention(qc),
  })
  return { ...mutation, confirmation: retentionConfirmation(mutation.error) }
}

/** Request an export (FR-011): 202 new, 200 the open one. */
export function useExportRequestMutation() {
  const qc = useQueryClient()
  return useMutation<ExportResult, Error, void>({
    mutationFn: () => requestExport(),
    retry: 0,
    onSuccess: () => invalidateRetention(qc),
  })
}

/** Where the deletion request lands: the sign-in screen states `deletionSignedOutNotice` (FR-013). */
export const ACCOUNT_CLOSED_PATH = '/login?notice=account-closed'

/**
 * Request the deletion (FR-013). On the 202 the session is gone: every cached query is dropped (no
 * tenant-scoped data survives in the tab; TEN.1) and the router leaves for the sign-in notice. A refusal
 * (the 403 `reauthentication-failed`, the 400 name mismatch) clears nothing and navigates nowhere.
 */
export function useDeleteAccountMutation() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  return useMutation<DeletionResult, Error, DeletionBody>({
    mutationFn: (body) => requestDeletion(body),
    retry: 0,
    onSuccess: () => {
      clearSessionCache(qc)
      void navigate(ACCOUNT_CLOSED_PATH, { replace: true })
    },
  })
}
